<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch, watchEffect } from "vue";
import CustomVideo from "../../../components/CustomVideo.vue";
import RightButton from "../../../components/room/RightButton.vue";
import LeftButton from "../../../components/room/LeftButton.vue";
import {
  currentSpeakers,
  usersIdInPage,
} from "../../../states/currentSpeakers";
import {
  resetTilingSpeakers,
  setSpeaker,
  tilingSpeakers,
} from "../../../states/tilingSpeakers";
import {
  UserInRoom,
  getUserById,
  usersState,
} from "../../../states/usersState";
import { ARROW_WIDTH, getSizes, SizeResponse } from "../../../utils/appSizes";
import { init } from "../../../states/media/state";

const sizes: SizeResponse = reactive({
  width: 300,
  height: 168.75,
  userPerPage: 1,
  padding: {
    top: 20,
    bottom: 20,
    left: 20,
    right: 20
  },
  gap: 24,
  arrowGap: 12,
  pages: 1
})

const recalc = () => {
  const area = tArea.value;
  if(!area) return;
  const width = area.clientWidth;
  const height = area.clientHeight;
  if(width === 0 || height === 0) return;
  const users = usersState.users.length;
  const newSizes = getSizes({
    width,
    height,
    userCount: users
  })
  Object.assign(sizes, newSizes);
}

const page = ref(0);
const tArea = ref<HTMLElement | null>(null);
let robs = new ResizeObserver(recalc);
watchEffect(() => {
  if (tArea.value) {
    //swipes
    let startX = 0;
    let endX = 0;
    tArea.value.addEventListener("touchstart", (event) => {
      startX = event.changedTouches[0].screenX;
    });

    tArea.value.addEventListener("touchend", (event) => {
      endX = event.changedTouches[0].screenX;
      handleSwipe();
    });

    const handleSwipe = () => {
      if (Math.abs(startX - endX) > 100) {
        if (startX > endX) {
          pageNext();
        } else {
          pagePrev();
        }
      }
    };

    robs.observe(tArea.value);
  } else {
    robs.disconnect();
  }
});

function pagePrev() {
  if (sizes.pages > 1 && page.value > 0) {
    page.value -= 1;
  }
}

function pageNext() {
  if (
    sizes.pages > 1 &&
    page.value < sizes.pages - 1
  ) {
    page.value += 1;
  }
}

watchEffect(() => {
  if (page.value >= sizes.pages) {
    page.value = sizes.pages - 1;
  }
});
watch(page, () => {
  resetTilingSpeakers();
});

const usersStatic = computed(() => {
  const start = page.value * sizes.userPerPage;
  const end = start + sizes.userPerPage;
  if (
    sizes.pages > 1 &&
    page.value === sizes.pages - 1
  ) {
    return usersState.users.slice(-sizes.userPerPage);
  } else {
    return usersState.users.slice(start, end);
  }
});
watch(() => usersState.users.length, () => {
  recalc();
});
watch(
  () => currentSpeakers,
  () => {
    for (let i = 0; i < currentSpeakers.length; i++) {
      setSpeaker(currentSpeakers[i], currentSpeakers.length - 1 - i);
    }
  },
  { deep: true },
);
const usersWithSpeaker = computed(() => {
  const res: UserInRoom[] = [...usersStatic.value];
  const staticuids = res.map((u) => u.id);
  const speakersUniq: string[] = [];
  const speakersExist: string[] = [];
  tilingSpeakers.forEach((s) => {
    if (staticuids.includes(s)) {
      speakersExist.push(s);
    } else {
      speakersUniq.push(s);
    }
  });
  for (let i = res.length - 1; i >= 0; i--) {
    if (speakersExist.includes(res[i].id)) {
      continue;
    } else if (speakersUniq.length > 0) {
      const speakerId = speakersUniq.shift() as string;
      const speaker = getUserById(speakerId);
      if (speaker) {
        res[i] = speaker;
      } else {
        continue;
      }
    } else {
      break;
    }
  }
  return res;
});
watchEffect(() => {
  if (usersStatic.value) {
    usersIdInPage.length = 0;
    usersStatic.value.forEach((u) => {
      usersIdInPage.push(u.id);
    });
  }
});
init()
onBeforeUnmount(() => {
  resetTilingSpeakers();
  usersIdInPage.length = 0;
});
</script>
<template>
  <div
    class="all-items"
    ref="tArea"
    :style="{
      padding: `${sizes.padding.top}px ${sizes.padding.right}px ${sizes.padding.bottom}px ${sizes.padding.left}px`,
      gap: sizes.arrowGap + 'px',
    }"
  >
    <div
      v-if="sizes.pages > 1"
      class="left-arrow"
      :style="{
        width: ARROW_WIDTH + 'px',
        minWidth: ARROW_WIDTH + 'px',
        maxWidth: ARROW_WIDTH + 'px',
      }"
    >
      <LeftButton v-if="page !== 0" @click="page -= 1" />
    </div>
    <div
      class="tiling-area"
      id="tiling-area"
      :style="{
        gap: `${sizes.gap}px ${sizes.gap}px`,
      }"
    >
      <CustomVideo
        v-for="(user) in usersWithSpeaker"
        :user-id="user.id"
        :avatar="user.avatar"
        :displayed-name="user.displayedName"
        :size="{
          width: sizes.width,
          height: sizes.height
        }"
        :key="`vel-${user.id}`"
      />
    </div>
    <div
      v-if="sizes.pages > 1"
      class="right-arrow"
      :style="{
        width: ARROW_WIDTH + 'px',
        minWidth: ARROW_WIDTH + 'px',
        maxWidth: ARROW_WIDTH + 'px',
      }"
    >
      <RightButton v-if="page !== sizes.pages - 1" @click="page += 1" />
    </div>
  </div>
</template>
<style scoped>
.all-items {
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  display: flex;
}

.right-arrow,
.left-arrow {
  height: 100%;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.tiling-area {
  width: 100px;
  flex-grow: 1;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  justify-items: center;
  align-content: center;
}
</style>
